import { Component, computed, input } from '@angular/core';
import {
  LucideDynamicIcon,
  LucideIconData,
  LucideIconInput,
  icons,
  isLucideIconComponent,
} from '@lucide/angular';

interface LucideIconComponent {
  icon: LucideIconData;
}

@Component({
  selector: 'app-dynamic-icon',
  standalone: true,
  imports: [LucideDynamicIcon],
  template: `
    @if (resolvedIcon(); as iconValue) {
      <svg [lucideIcon]="iconValue" [class]="cssClass()"></svg>
    } @else {
      <div
        [class]="cssClass()"
        class="d-inline-flex align-items-center justify-content-center"
      >
        <div class="fallback-dot"></div>
      </div>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .fallback-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: currentColor;
      opacity: 0.35;
    }
  `,
})
export class DynamicIconComponent {
  readonly name = input<string>('');
  readonly icon = input<LucideIconInput | null>(null);
  readonly cssClass = input<string>('');

  readonly resolvedIcon = computed<LucideIconInput | null>(() => {
    const directIcon = this.icon();
    if (directIcon) {
      return this.resolveIconInput(directIcon);
    }

    return this.resolveIconInput(this.name());
  });

  private resolveIconInput(
    value: LucideIconInput | null | undefined,
  ): LucideIconInput | null {
    if (!value) {
      return null;
    }

    if (typeof value !== 'string') {
      return value;
    }

    const iconName = value.trim();
    if (!iconName) {
      return null;
    }

    const iconMap = icons as Record<string, unknown>;
    const candidates = this.getLucideExportCandidates(iconName);

    for (const candidate of candidates) {
      const entry = iconMap[candidate];
      if (entry && isLucideIconComponent(entry)) {
        return (entry as LucideIconComponent).icon;
      }
    }

    return null;
  }

  /**
   * Lucide Angular exports icons with PascalCase keys, usually prefixed by `Lucide`.
   * API data commonly arrives as kebab-case names from lucide.dev, so we normalize
   * those names into the export candidates used by the package.
   */
  private getLucideExportCandidates(name: string): string[] {
    const pascalName = this.toPascalCaseFromKebab(name);
    return [name, `Lucide${pascalName}`, pascalName];
  }

  private toPascalCaseFromKebab(name: string): string {
    return name
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');
  }
}
